import {
    isImageUploadFile,
    resolveOssFileSuffix,
    uploadFileToOSS,
    SIMPLE_UPLOAD_MAX_BYTES,
} from '~/utils/ossUploader.js'
import {
    fetchWithTimeout,
    isRetryableUploadError,
    withUploadRetry,
} from '~/utils/uploadRetry.js'

export const CHUNK_SIZE_BYTES = 5 * 1024 * 1024;
export const DEFAULT_UPLOAD_CONCURRENCY = 3;

let verifiedFetchPromise = null;

function loadVerifiedFetch() {
    if (!verifiedFetchPromise) {
        verifiedFetchPromise = import("~/utils/requestVerification").then(({ verifiedFetch }) => verifiedFetch);
    }
    return verifiedFetchPromise;
}

function createUploadError(uploadStage, failReason, message, cause, extras = {}) {
    const error = new Error(message || failReason || "upload failed");
    error.uploadStage = uploadStage;
    error.failReason = failReason;
    if (cause) error.cause = cause;
    if (extras.httpStatus != null) error.httpStatus = Number(extras.httpStatus);
    if (extras.businessCode != null) error.businessCode = Number(extras.businessCode);
    return error;
}

function normalizeUploadError(error, uploadStage, fallbackReason = "unknown") {
    if (error?.uploadStage && error?.failReason) return error;
    const message = error?.message ? String(error.message) : String(error || "");
    if (error?.failReason === "timeout" || error?.name === "TimeoutError") {
        return createUploadError(uploadStage, "timeout", message || "request timed out", error);
    }
    const failReason =
        /network|fetch|load failed/i.test(message) ? "network_error" : fallbackReason;
    const normalized = createUploadError(
        uploadStage,
        failReason,
        message || "upload failed",
        error,
    );
    if (error?.httpStatus != null) normalized.httpStatus = Number(error.httpStatus);
    if (error?.businessCode != null) normalized.businessCode = Number(error.businessCode);
    return normalized;
}

function failReasonForHttpStatus(status, fallback = "api_error") {
    const code = Number(status);
    if (code === 401 || code === 403) return "auth_required";
    return fallback;
}

function notifyUploadStage(onStage, uploadStage) {
    if (typeof onStage === "function") onStage(uploadStage);
}

function resolveUploadOptions(onProgress, options) {
    if (onProgress && typeof onProgress === "object") {
        return {
            onProgress: typeof onProgress.onProgress === "function" ? onProgress.onProgress : null,
            options: onProgress,
        };
    }
    return {
        onProgress: typeof onProgress === "function" ? onProgress : null,
        options: options && typeof options === "object" ? options : {},
    };
}

// Some S3/R2 compatible services expose ETag in the Network tab,
// but due to CORS "Access-Control-Expose-Headers" the browser fetch()
// may not let us read response headers. In that case we compute
// part ETag from the chunk MD5 as a fallback.
function md5HexFromBytes(bytes) {
    const K = [
        0xd76aa478, 0xe8c7b756, 0x242070db, 0xc1bdceee, 0xf57c0faf,
        0x4787c62a, 0xa8304613, 0xfd469501, 0x698098d8, 0x8b44f7af,
        0xffff5bb1, 0x895cd7be, 0x6b901122, 0xfd987193, 0xa679438e,
        0x49b40821, 0xf61e2562, 0xc040b340, 0x265e5a51, 0xe9b6c7aa,
        0xd62f105d, 0x02441453, 0xd8a1e681, 0xe7d3fbc8, 0x21e1cde6,
        0xc33707d6, 0xf4d50d87, 0x455a14ed, 0xa9e3e905, 0xfcefa3f8,
        0x676f02d9, 0x8d2a4c8a, 0xfffa3942, 0x8771f681, 0x6d9d6122,
        0xfde5380c, 0xa4beea44, 0x4bdecfa9, 0xf6bb4b60, 0xbebfbc70,
        0x289b7ec6, 0xeaa127fa, 0xd4ef3085, 0x04881d05, 0xd9d4d039,
        0xe6db99e5, 0x1fa27cf8, 0xc4ac5665, 0xf4292244, 0x432aff97,
        0xab9423a7, 0xfc93a039, 0x655b59c3, 0x8f0ccc92, 0xffeff47d,
        0x85845dd1, 0x6fa87e4f, 0xfe2ce6e0, 0xa3014314, 0x4e0811a1,
        0xf7537e82, 0xbd3af235, 0x2ad7d2bb, 0xeb86d391,
    ];

    const S = [
        7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22,
        5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20,
        4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23,
        6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21,
    ];

    function leftRotate(x, c) {
        return ((x << c) | (x >>> (32 - c))) >>> 0;
    }

    // Prepare message (padding) as 32-bit little-endian words.
    const len = bytes.length;
    const bitLen = len * 8;
    const numWords = Math.ceil((len + 9) / 64) * 16;
    const words = new Uint32Array(numWords);

    for (let i = 0; i < len; i++) {
        words[i >>> 2] |= bytes[i] << ((i % 4) * 8);
    }

    words[len >>> 2] |= 0x80 << ((len % 4) * 8);
    // last 64-bit length in bits (little-endian)
    words[numWords - 2] = bitLen & 0xffffffff;
    words[numWords - 1] = Math.floor(bitLen / 0x100000000) >>> 0;

    let a = 0x67452301;
    let b = 0xefcdab89;
    let c = 0x98badcfe;
    let d = 0x10325476;

    for (let i = 0; i < numWords; i += 16) {
        const aa = a;
        const bb = b;
        const cc = c;
        const dd = d;

        for (let j = 0; j < 64; j++) {
            let f;
            let g;
            if (j < 16) {
                f = (b & c) | (~b & d);
                g = j;
            } else if (j < 32) {
                f = (d & b) | (~d & c);
                g = (5 * j + 1) % 16;
            } else if (j < 48) {
                f = b ^ c ^ d;
                g = (3 * j + 5) % 16;
            } else {
                f = c ^ (b | ~d);
                g = (7 * j) % 16;
            }

            const tmp = d;
            d = c;
            c = b;
            const sum = (a + f + K[j] + words[i + g]) >>> 0;
            b = (b + leftRotate(sum, S[j])) >>> 0;
            a = tmp;
        }

        a = (a + aa) >>> 0;
        b = (b + bb) >>> 0;
        c = (c + cc) >>> 0;
        d = (d + dd) >>> 0;
    }

    function wordToHexLE(word) {
        let out = "";
        for (let k = 0; k < 4; k++) {
            const byte = (word >>> (k * 8)) & 0xff;
            out += byte.toString(16).padStart(2, "0");
        }
        return out;
    }

    return wordToHexLE(a) + wordToHexLE(b) + wordToHexLE(c) + wordToHexLE(d);
}

/**
 * Step 1: request multipart upload pre-signed URLs.
 * GET /api/aigc/pre_interface/generate-multipart-pre-signed-url
 * Query: file_name, file_size, part_size (bytes, strings)
 * Header: authorization + request verification headers are added by verifiedFetch.
 */
export async function getMultipartPreSignedUrl(fileName, fileSize, partSize) {
    if (!fileName) {
        throw new Error("file_name is required");
    }
    const size = Number(fileSize);
    const part = Number(partSize);
    if (!Number.isFinite(size) || size <= 0) {
        throw new Error("file_size must be a positive number");
    }
    if (!Number.isFinite(part) || part <= 0) {
        throw new Error("part_size must be a positive number");
    }

    const query = new URLSearchParams({
        file_name: fileName,
        file_size: String(size),
        part_size: String(part),
    });

    let response;
    try {
        // _signRetry: 禁用 verifiedFetch 对 100060 的零延迟内置重试，改由 withUploadRetry 统一退避
        response = await fetchWithTimeout(async (signal) =>
            (await loadVerifiedFetch())(
                `/api/aigc/pre_interface/v2-generate-multipart-pre-signed-url?${query.toString()}`,
                {
                    method: "GET",
                    _signRetry: true,
                    ...(signal ? { signal } : {}),
                },
            ),
        );
    } catch (error) {
        if (error?.failReason === "timeout" || error?.name === "TimeoutError") {
            throw createUploadError("pre_sign", "timeout", error.message, error);
        }
        throw normalizeUploadError(error, "pre_sign", "network_error");
    }

    if (!response.ok) {
        const text = await response.text();
        throw createUploadError(
            "pre_sign",
            failReasonForHttpStatus(response.status, "api_error"),
            `generate-multipart-pre-signed-url failed: ${response.status} ${text}`,
            null,
            { httpStatus: response.status },
        );
    }

    const contentType = response.headers.get("content-type") || "";
    if (!contentType.includes("application/json")) {
        throw createUploadError(
            "pre_sign",
            "api_error",
            "content-type not includes application/json",
        );
    }

    return await response.json();
}

/**
 * Step 4: complete multipart upload by upload_id + parts.
 * POST /api/aigc/pre_interface/complete-pre-signed-multipart-upload
 * Body: { file_relative_path, upload_id, parts }
 */
export async function completeMultipartUpload({
    fileRelativePath,
    uploadId,
    parts,
}) {
    if (!fileRelativePath) {
        throw new Error("file_relative_path is required");
    }
    if (!uploadId) {
        throw new Error("upload_id is required");
    }
    if (!Array.isArray(parts) || !parts.length) {
        throw new Error("parts is required");
    }

    let response;
    try {
        response = await (await loadVerifiedFetch())(
            "/api/aigc/pre_interface/complete-pre-signed-multipart-upload",
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    file_relative_path: fileRelativePath,
                    upload_id: uploadId,
                    parts,
                }),
                _signRetry: true,
            },
        );
    } catch (error) {
        throw normalizeUploadError(error, "complete_upload", "network_error");
    }

    if (!response.ok) {
        const text = await response.text();
        throw createUploadError(
            "complete_upload",
            failReasonForHttpStatus(response.status, "api_error"),
            `complete-pre-signed-multipart-upload failed: ${response.status} ${text}`,
            null,
            { httpStatus: response.status },
        );
    }
    const contentType = response.headers.get("content-type") || "";
    if (!contentType.includes("application/json")) {
        throw createUploadError(
            "complete_upload",
            "api_error",
            "content-type not includes application/json",
        );
    }

    const payload = await response.json();
    const businessCode = Number(payload?.code);
    if (Number.isFinite(businessCode) && businessCode !== 100000) {
        const failReason = businessCode === 100060 ? "sign_failed" : "api_error";
        throw createUploadError(
            "complete_upload",
            failReason,
            payload?.message || `complete-upload business code ${businessCode}`,
            null,
            { businessCode },
        );
    }
    return payload;
}

function pickPartUploadUrls(preSignResponse) {
    const data = preSignResponse?.data || {};
    const candidates = [
        preSignResponse?.pre_signed,
        data.shards,
        data.shard_urls,
        data.pre_signed,
        data.part_urls,
        data.parts,
        data.urls,
        data.signed_urls,
        data.oss_signed_urls,
        preSignResponse?.shards,
        preSignResponse?.urls,
    ];

    const firstValid = candidates.find((arr) => Array.isArray(arr) && arr.length);
    if (!firstValid) return [];

    return firstValid
        .map((item) => {
            if (typeof item === "string") return item;
            return item?.put || item?.upload_url || item?.signed_url || item?.url || "";
        })
        .filter(Boolean);
}

function pickFinalResourceUrl(preSignResponse) {
    const data = preSignResponse?.data || {};

    return (
        data.file_url ||
        data.url ||
        data.get ||
        data.object_url ||
        data.resource_url ||
        preSignResponse?.url ||
        ""
    );
}

function pickUploadId(preSignResponse) {
    const data = preSignResponse?.data || {};
    return data.upload_id || preSignResponse?.upload_id || "";
}

function pickFileRelativePath(preSignResponse) {
    const data = preSignResponse?.data || {};
    return (
        data.file_relative_path ||
        data.file_path ||
        preSignResponse?.file_relative_path ||
        ""
    );
}

/**
 * Upload file:
 * - image ≤ 5MB → one-shot OSS signed PUT (`uploadFileToOSS`)
 * - otherwise → multipart with fixed 5MB chunks
 * onProgress receives percent number in [0, 100].
 */
export async function uploadFileByChunks(file, onProgress, options = {}) {
    if (!file) throw new Error("file is required");
    const resolved = resolveUploadOptions(onProgress, options);
    const progressHandler = resolved.onProgress;
    const uploadOptions = resolved.options;
    const onStage = uploadOptions.onStage;

    // 图片 ≤ 5MB：一次性签名上传；音频 / 大图仍走分片
    if (isImageUploadFile(file) && Number(file.size) <= SIMPLE_UPLOAD_MAX_BYTES) {
        notifyUploadStage(onStage, "pre_sign");
        if (progressHandler) progressHandler(0);
        let url;
        try {
            const f_suffixs = [resolveOssFileSuffix(file)];
            url = await uploadFileToOSS(file, f_suffixs);
        } catch (error) {
            throw normalizeUploadError(
                error,
                error?.uploadStage || "pre_sign",
                error?.failReason || "api_error",
            );
        }
        if (!url) {
            throw createUploadError("resolve_url", "no_url", "simple OSS upload returned empty url");
        }
        notifyUploadStage(onStage, "resolve_url");
        if (progressHandler) progressHandler(100);
        return {
            url,
            uploadId: "",
            fileRelativePath: "",
            parts: [],
            completeResponse: null,
            preSignResponse: null,
            shardsNumber: 1,
            mode: "simple",
        };
    }

    const concurrency = Math.max(
        1,
        Math.min(
            8,
            Math.round(Number(uploadOptions.concurrency || DEFAULT_UPLOAD_CONCURRENCY)) ||
                DEFAULT_UPLOAD_CONCURRENCY,
        ),
    );

    const shardsNumber = Math.max(1, Math.ceil(file.size / CHUNK_SIZE_BYTES));
    notifyUploadStage(onStage, "pre_sign");
    const { preSignResponse, uploadUrls } = await withUploadRetry(async () => {
        const preSignResponse = await getMultipartPreSignedUrl(
            file.name,
            file.size,
            CHUNK_SIZE_BYTES,
        );

        const businessCode = Number(preSignResponse?.code);
        if (Number.isFinite(businessCode) && businessCode !== 100000) {
            const failReason = businessCode === 100060 ? "sign_failed" : "api_error";
            throw createUploadError(
                "pre_sign",
                failReason,
                preSignResponse?.message || `pre-sign business code ${businessCode}`,
                null,
                { businessCode },
            );
        }

        const uploadUrls = pickPartUploadUrls(preSignResponse);
        if (!uploadUrls.length) {
            throw createUploadError(
                "pre_sign",
                "sign_failed",
                "No shard upload URLs returned by pre-sign API",
            );
        }
        if (uploadUrls.length < shardsNumber) {
            throw createUploadError(
                "pre_sign",
                "sign_failed",
                `Shard URL count mismatch: expect ${shardsNumber}, got ${uploadUrls.length}`,
            );
        }

        return { preSignResponse, uploadUrls };
    });

    let uploadedBytes = 0;
    let nextPartIndex = 0;
    let firstError = null;
    const parts = new Array(shardsNumber);
    const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
    if (progressHandler) progressHandler(0);

    async function uploadPart(i) {
        const start = i * CHUNK_SIZE_BYTES;
        const end = Math.min(start + CHUNK_SIZE_BYTES, file.size);
        const chunk = file.slice(start, end);

        await withUploadRetry(
            async () => {
                let res;
                try {
                    res = await fetch(uploadUrls[i], {
                        method: "PUT",
                        body: chunk,
                        ...(controller ? { signal: controller.signal } : {}),
                    });
                } catch (error) {
                    throw normalizeUploadError(error, "chunk_upload", "network_error");
                }
                if (!res.ok) {
                    throw createUploadError(
                        "chunk_upload",
                        failReasonForHttpStatus(res.status, "chunk_upload_failed"),
                        `chunk ${i + 1} upload failed: ${res.status}`,
                        null,
                        { httpStatus: res.status },
                    );
                }

                let etag = res.headers.get("ETag") || res.headers.get("etag");
                if (!etag) {
                    // Fallback for browsers where ETag is not exposed via CORS.
                    // MD5 of the part equals the part ETag for S3-compatible services.
                    // (Avoids failing the entire upload flow.)
                    const chunkBytes = new Uint8Array(await chunk.arrayBuffer());
                    etag = md5HexFromBytes(chunkBytes);
                } else {
                    etag = etag.replace(/^W\//i, "").replace(/"/g, "");
                }
                parts[i] = {
                    PartNumber: i + 1,
                    ETag: etag,
                };

                uploadedBytes += chunk.size;
                if (progressHandler) {
                    const percent = Math.min(95, Math.round((uploadedBytes / file.size) * 95));
                    progressHandler(percent);
                }
            },
            {
                signal: controller?.signal,
                isRetryable: (error) =>
                    !firstError && isRetryableUploadError(error),
            },
        );
    }

    async function uploadWorker() {
        while (!firstError) {
            const i = nextPartIndex;
            nextPartIndex += 1;
            if (i >= shardsNumber) return;
            try {
                await uploadPart(i);
            } catch (error) {
                if (!firstError) {
                    firstError = normalizeUploadError(
                        error,
                        "chunk_upload",
                        "chunk_upload_failed",
                    );
                    if (controller) controller.abort();
                }
                throw firstError;
            }
        }
    }

    notifyUploadStage(onStage, "chunk_upload");
    await Promise.all(
        Array.from({ length: Math.min(concurrency, shardsNumber) }, () => uploadWorker()),
    );

    const finalUrl = pickFinalResourceUrl(preSignResponse);
    const uploadId = pickUploadId(preSignResponse);
    const fileRelativePath = pickFileRelativePath(preSignResponse);
    notifyUploadStage(onStage, "complete_upload");
    const completeResponse = await withUploadRetry(async () =>
        completeMultipartUpload({
            fileRelativePath,
            uploadId,
            parts: parts.filter(Boolean).sort((a, b) => a.PartNumber - b.PartNumber),
        }),
    ).catch((error) => {
        throw normalizeUploadError(error, "complete_upload", "complete_upload_failed");
    });
    const completedUrl = pickFinalResourceUrl(completeResponse) || finalUrl;
    notifyUploadStage(onStage, "resolve_url");
    if (progressHandler) progressHandler(100);

    return {
        url: completedUrl,
        uploadId,
        fileRelativePath,
        parts,
        completeResponse,
        preSignResponse,
        shardsNumber,
        mode: "multipart",
    };
}
